import { differenceInDays, eachDayOfInterval, endOfDay, format, startOfDay, } from "date-fns";
import { first, groupBy, last, orderBy } from "lodash";
import pipe from "../pipe";
function dateFormatter(d) {
    return format(d, "yyyy-MM-dd");
}
export default function fillInterpolatedData(start, end, array, options) {
    if (array.length === 0) {
        return eachDayOfInterval({ start, end }, options).map((date) => ({
            date,
            value: 0,
        }));
    }
    const sortedAsc = orderBy(array, [(x) => x.date.valueOf()], ["asc"]);
    const grouped = groupBy(sortedAsc, (x) => dateFormatter(x.date));
    // Reset the date to the start of the day so that `date.valueOf() < firstPoint.date.valueOf()` can match
    const firstPoint = pipe(first(sortedAsc) ?? null, (d) => ({ ...d, date: startOfDay(d.date) }));
    const lastPoint = last(sortedAsc) ?? null;
    if (firstPoint === null || lastPoint === null) {
        throw new Error("Somehow we have have a non empty array with empty data");
    }
    // We have to look at the whole array because we may need to interpolate from
    // outside the [start, end] to have good data on the start side
    const fullArrayInterval = eachDayOfInterval({ start: firstPoint.date, end: lastPoint.date }, options);
    const knownDataPoints = fullArrayInterval.map((date) => {
        const matchedData = grouped[dateFormatter(date)];
        if (matchedData === undefined) {
            return null;
        }
        const ordered = orderBy(matchedData, [(a) => a.date.valueOf()], ["asc"]);
        // use the last point if we have many
        const lastValue = last(ordered) ?? null;
        if (lastValue === null) {
            return null;
        }
        return { date, value: lastValue.value };
    });
    // Create known pairs that we need to interpolate between
    const [matches, remainingPoint] = knownDataPoints.reduce(([matchedPoints, lastKnownPoint], cur) => {
        if (cur === null) {
            return [matchedPoints, lastKnownPoint];
        }
        if (lastKnownPoint === null) {
            return [matchedPoints, cur];
        }
        else {
            return [[...matchedPoints, [lastKnownPoint, cur]], cur];
        }
    }, [[], null]);
    const buildInterpolation = fullArrayInterval.map((date) => {
        const matchingPair = matches.find(([s, e]) => startOfDay(s.date).valueOf() <= date.valueOf() &&
            date.valueOf() <= endOfDay(e.date).valueOf());
        if (matchingPair === undefined) {
            if (remainingPoint === null) {
                return { date, value: 0 };
            }
            else if (date.valueOf() >= startOfDay(remainingPoint.date).valueOf()) {
                return { date, value: remainingPoint.value };
            }
            else {
                return { date, value: 0 };
            }
        }
        const diff = matchingPair[1].value - matchingPair[0].value;
        const daysBetween = differenceInDays(matchingPair[1].date, matchingPair[0].date);
        const deltaPerDay = diff / daysBetween;
        const daysSinceStart = differenceInDays(date, matchingPair[0].date);
        const growthSinceStart = daysSinceStart * deltaPerDay;
        return { date, value: matchingPair[0].value + growthSinceStart };
    });
    const interpolatedDB = groupBy(buildInterpolation, (x) => dateFormatter(x.date));
    const requestedInterval = eachDayOfInterval({ start, end }, options);
    return requestedInterval.map((date) => {
        if (date.valueOf() < firstPoint.date.valueOf()) {
            return { date, value: 0 };
        }
        if (lastPoint.date.valueOf() < date.valueOf()) {
            return { date, value: lastPoint.value };
        }
        const interpolated = interpolatedDB[dateFormatter(date)];
        if (interpolated === undefined || interpolated[0] === undefined) {
            return { date, value: 0 };
        }
        return { date, value: interpolated[0].value };
    });
}
//# sourceMappingURL=fillInterpolatedData.js.map